import { useMemo, useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import ACL_RELATIONSHIPS from "acl-relationships";
import useAuthorized from "hooks/useAuthorized";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  BaseForm,
  FormColorPicker,
  FormInput,
  FormMultiSelect,
  FormRecordPicker,
  FormTextarea,
  FormToggle,
} from "components/BaseForm";
import { tableTimestamp } from "components/helpers";
import tagConstants from "constants/Tag";
import { getRandomColor } from "helpers/color";
import { useTagMutations } from "hooks/useTagManagementTable";
import CustomTableService from "services/CustomTable";
import RoleService from "services/Role";
import TagService from "services/Tag";
import TagCategoryService from "services/TagCategory";
import TagColorPreview from "routes/tags/components/TagColorPreview";
import TagMetadata from "./components/TagMetadata";

const { metadataTypes } = tagConstants;

const TAGGABLE_MODEL_OPTIONS = Object.entries(tagConstants.taggableModels).map(
  ([key, text]) => ({ key, value: key, text })
);

const ALL_ROLES_OPTION = { key: "all", value: 0, text: "All Roles" };

// "All Roles" (0) and "All Models" are exclusive: selecting them clears the
// rest, matching the legacy Semantic form's collapse-on-submit behavior.
const collapse = (values, sentinel) =>
  values?.includes(sentinel) ? [sentinel] : (values ?? []);

const isArrayShaped = field =>
  field.field_type === metadataTypes.list ||
  (field.field_type === metadataTypes.categorical &&
    field.field_options?.type === "multi");

// tag_metadata_fields.default_value is a plain String(2048) column, so
// array-shaped defaults round-trip as a Postgres array literal, e.g. "{a,b}".
const parseArrayDefault = value => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  return (value || "{}")
    .replace(/^{|}$/g, "")
    .split(",")
    .filter(Boolean)
    .map(e => e.replace(/^[\\"]+|[\\"]+$/g, ""));
};

const normalizeDefault = field => {
  if (field.field_type === metadataTypes.boolean) {
    return field.default_value === true || field.default_value === "true";
  }
  if (isArrayShaped(field)) {
    return parseArrayDefault(field.default_value);
  }
  return field.default_value ?? "";
};

const normalizeMetadataField = field => ({
  id: field.id,
  field_name: field.field_name ?? "",
  field_type: field.field_type,
  default_value: normalizeDefault(field),
  allow_override: !!field.allow_override,
  required: !!field.required,
  read_roles: field.read_roles?.length ? field.read_roles : [0],
  edit_roles: field.edit_roles?.length ? field.edit_roles : [0],
  field_options: field.field_options ?? {},
});

const metadataFieldSchema = z.object({
  id: z.number().optional(),
  field_name: z.string().min(1, "Field name is required"),
  field_type: z.string(),
  default_value: z.any().nullish(),
  allow_override: z.boolean(),
  required: z.boolean(),
  read_roles: z.array(z.number()).min(1, "Select at least one role"),
  edit_roles: z.array(z.number()).min(1, "Select at least one role"),
  field_options: z.any().optional(),
  order: z.number().optional(),
});

const schema = z.object({
  enabled: z.boolean(),
  name: z.string().min(1, "Name is required"),
  description: z.string().min(1, "Description is required"),
  color: z.string().min(1, "Color is required"),
  taggable_models: z.array(z.string()).min(1, "Select at least one model"),
  read_roles: z.array(z.number()).min(1, "Select at least one role"),
  edit_roles: z.array(z.number()).min(1, "Select at least one role"),
  tag_category_id: z.number().nullable(),
  tag_metadata_fields: z.array(metadataFieldSchema),
});

const fetchCategories = search =>
  TagCategoryService.getTagCategoryForFilters(
    search ? { search_query: search } : null
  );

const TagForm = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = mode === "edit";
  const cloneId = searchParams.get("clone");
  const isClone = !isEdit && !!cloneId;
  const sourceId = isEdit ? id : cloneId;
  const [isConfirmationRequired, setIsConfirmationRequired] = useState(false);

  const canWrite = useAuthorized([
    isEdit ? ACL_RELATIONSHIPS.tag.edit : ACL_RELATIONSHIPS.tag.create,
  ]);

  const { create, update } = useTagMutations();

  const {
    data: sourceTag,
    isSuccess: tagLoaded,
    isError: tagError,
  } = useQuery({
    queryKey: ["tag", sourceId],
    queryFn: () => TagService.getTag(sourceId),
    enabled: !!sourceId,
    retry: false,
  });

  const {
    data: rolesData,
    isFetched: rolesFetched,
    isError: rolesError,
  } = useQuery({
    queryKey: ["roles-for-filters"],
    queryFn: () => RoleService.getRoles(),
    retry: false,
  });

  const {
    data: customTablesData,
    isFetched: tablesFetched,
    isError: tablesError,
  } = useQuery({
    queryKey: ["custom-tables-for-tag-form"],
    queryFn: () => CustomTableService.list({ per_page: 200 }),
    retry: false,
  });

  const userRoles = useMemo(
    () => [
      ALL_ROLES_OPTION,
      ...(rolesData ?? []).map(role => ({
        key: role.id,
        value: role.id,
        text: role.name,
      })),
    ],
    [rolesData]
  );

  const modelOptions = useMemo(
    () => [
      ...TAGGABLE_MODEL_OPTIONS,
      ...(customTablesData?.data ?? []).map(table => ({
        key: `custom_table:${table.slug}`,
        value: `custom_table:${table.slug}`,
        text: table.display_name,
      })),
    ],
    [customTablesData]
  );

  const defaultColor = useMemo(() => getRandomColor(), []);

  const defaultValues = useMemo(() => {
    const tag = sourceId ? sourceTag : null;
    return {
      enabled: tag?.enabled ?? true,
      name: isClone ? "" : (tag?.name ?? ""),
      description: tag?.description ?? "",
      color: tag?.color ?? defaultColor,
      taggable_models: tag?.taggable_models ?? [],
      read_roles: tag?.read_roles?.length ? tag.read_roles.map(r => r.id) : [0],
      edit_roles: tag?.edit_roles?.length ? tag.edit_roles.map(r => r.id) : [0],
      tag_category_id: tag?.tag_category_id ?? null,
      tag_metadata_fields: (tag?.tag_metadata_fields ?? []).map(
        normalizeMetadataField
      ),
    };
  }, [sourceTag, sourceId, isClone, defaultColor]);

  const historySubtitle = useMemo(() => {
    if (!isEdit || !sourceTag) return undefined;
    const editor = sourceTag.updated_by_user?.full_name;
    if (editor && sourceTag.updated_at) {
      const when = tableTimestamp(sourceTag.updated_at);
      return `${sourceTag.name}, last edited ${when} by ${editor}`;
    }
    if (sourceTag.created_at) {
      const when = tableTimestamp(sourceTag.created_at);
      const creator = sourceTag.created_by_user?.full_name;
      const base = `${sourceTag.name}, created ${when}`;
      return creator ? `${base} by ${creator}` : base;
    }
    return undefined;
  }, [isEdit, sourceTag]);

  const buildPayload = values => ({
    name: values.name,
    description: values.description,
    color: values.color,
    enabled: values.enabled,
    taggable_models: collapse(values.taggable_models, "all_models"),
    read_roles: collapse(values.read_roles, 0),
    edit_roles: collapse(values.edit_roles, 0),
    tag_category_id: values.tag_category_id,
    tag_metadata_fields: values.tag_metadata_fields.map((field, index) => ({
      ...field,
      default_value:
        field.field_type === metadataTypes.boolean
          ? String(field.default_value)
          : field.default_value,
      read_roles: collapse(field.read_roles, 0),
      edit_roles: collapse(field.edit_roles, 0),
      order: index,
    })),
  });

  const handleSubmit = async values => {
    const payload = buildPayload(values);
    if (isEdit) {
      await update.mutateAsync({
        id: sourceId,
        data: { ...payload, update_model_tags: isConfirmationRequired },
      });
    } else {
      await create.mutateAsync(payload);
    }
    toast.success(isEdit ? "Tag updated" : "Tag created");
    navigate("/settings/tags");
  };

  const handleCancel = () => navigate("/settings/tags");

  if (!canWrite) {
    return <Navigate to="/settings/tags" replace />;
  }

  if (tagError || rolesError || tablesError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-[var(--color-text-muted)]">
        <p>{"Couldn't load this tag. Please try again."}</p>
        <Link to="/settings/tags">Back to Tags</Link>
      </div>
    );
  }

  const ready = (!sourceId || tagLoaded) && rolesFetched && tablesFetched;

  if (!ready) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-[var(--color-text-muted)]">
        <Spinner />
      </div>
    );
  }

  const pageTitle = isEdit ? "Edit Tag" : isClone ? "Clone Tag" : "New Tag";

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-start tw:font-sans tw:text-[var(--color-text-primary)]">
      <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        <BaseForm
          enablePageTitle
          enablePageTitleActions
          pageTitle={pageTitle}
          pageSubtitle={historySubtitle}
          pageTitleActions={<FormToggle name="enabled" label="Enable Tag" />}
          backLink={{ to: "/settings/tags", label: "Back to Tags" }}
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          submitButtonText="Save"
          confirmSubmit={isConfirmationRequired}
          confirmText="Some metadata was modified. Update previously tagged records with the new default value(s)?"
        >
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
                <FormInput name="name" label="Name" required />
                <FormRecordPicker
                  name="tag_category_id"
                  label="Tag Category"
                  fetchOptions={fetchCategories}
                  onCreate={label =>
                    TagCategoryService.createTagCategory({ name: label })
                  }
                />
                <FormMultiSelect
                  name="taggable_models"
                  label="Taggable Models"
                  options={modelOptions}
                  required
                />
                <FormColorPicker
                  name="color"
                  label="Color"
                  required
                  preview={<TagColorPreview />}
                />
                <div className="tw:sm:col-span-2">
                  <FormTextarea
                    name="description"
                    label="Description"
                    required
                    rows={2}
                    placeholder="What is this tag for?"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Permissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
                <FormMultiSelect
                  name="edit_roles"
                  label="View and Edit"
                  options={userRoles}
                  required
                />
                <FormMultiSelect
                  name="read_roles"
                  label="View Only"
                  options={userRoles}
                  required
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Metadata</CardTitle>
            </CardHeader>
            <CardContent>
              <TagMetadata
                userRoles={userRoles}
                setConfirmationRequired={setIsConfirmationRequired}
                isEdit={isEdit}
              />
            </CardContent>
          </Card>
        </BaseForm>
      </div>
    </div>
  );
};

export default TagForm;
